import { useState, useEffect } from 'react';
import api from '@/lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Users, Package, CreditCard, Share2, TrendingUp, UserCheck, DollarSign, Clock, AlertTriangle, ArrowUpRight, ArrowDownRight } from 'lucide-react';
import { Link } from 'react-router-dom';

export default function AdminDashboard() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get('/admin/dashboard').then(r => { setData(r.data); setLoading(false); }).catch(() => setLoading(false));
  }, []);

  if (loading) return <div className="flex items-center justify-center h-64"><div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#2D6CDF]" /></div>;

  const stats = data?.stats || {};
  const fmt = (v) => new Intl.NumberFormat('id-ID').format(v || 0);

  const statCards = [
    { label: 'Total Tenants', value: stats.total_tenants, icon: Users, color: 'text-[#2D6CDF]', bg: 'bg-blue-50' },
    { label: 'Tenant Aktif', value: stats.active_tenants, icon: UserCheck, color: 'text-emerald-600', bg: 'bg-emerald-50' },
    { label: 'MRR', value: `Rp ${fmt(stats.mrr)}`, icon: TrendingUp, color: 'text-violet-600', bg: 'bg-violet-50', isRevenue: true },
    { label: 'Total Revenue', value: `Rp ${fmt(stats.total_revenue)}`, icon: DollarSign, color: 'text-teal-600', bg: 'bg-teal-50', isRevenue: true },
    { label: 'Pending Payment', value: stats.pending_payments, icon: Clock, color: 'text-amber-600', bg: 'bg-amber-50' },
    { label: 'Expiring Soon', value: stats.expiring_soon, icon: AlertTriangle, color: 'text-red-600', bg: 'bg-red-50' },
  ];

  const statusColor = (s) => {
    if (s === 'active') return 'bg-emerald-100 text-emerald-700';
    if (s === 'trial') return 'bg-amber-100 text-amber-700';
    if (s === 'pending') return 'bg-blue-100 text-blue-700';
    if (s === 'suspended' || s === 'expired') return 'bg-red-100 text-red-700';
    return 'bg-slate-100 text-slate-700';
  };

  return (
    <div data-testid="admin-dashboard">
      <div className="flex items-center justify-between mb-6">
        <h1 className="text-2xl md:text-3xl font-bold tracking-tight text-slate-900" style={{ fontFamily: 'Plus Jakarta Sans' }}>Dashboard Admin</h1>
        <div className="flex gap-2">
          <Link to="/admin/tenants">
            <Button variant="outline" size="sm">Kelola Tenant</Button>
          </Link>
          <Link to="/admin/billing">
            <Button size="sm" className="bg-[#2D6CDF] hover:bg-[#1A5BC4]">Billing</Button>
          </Link>
        </div>
      </div>

      {/* Stats Grid */}
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4 mb-8">
        {statCards.map(({ label, value, icon: Icon, color, bg, isRevenue }, i) => (
          <Card key={i} className="border-slate-100 shadow-sm hover:shadow-md transition-shadow" data-testid={`admin-stat-${i}`}>
            <CardContent className="p-4">
              <div className={`w-9 h-9 rounded-lg ${bg} flex items-center justify-center mb-3`}>
                <Icon size={18} className={color} />
              </div>
              <p className={`text-xl font-bold text-slate-900 ${isRevenue ? 'text-lg' : ''}`}>{value ?? 0}</p>
              <p className="text-xs text-slate-500 mt-1">{label}</p>
            </CardContent>
          </Card>
        ))}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Recent Tenants */}
        <Card className="border-slate-200 shadow-sm">
          <CardHeader className="flex flex-row items-center justify-between">
            <CardTitle className="text-lg">Tenant Terbaru</CardTitle>
            <Link to="/admin/tenants">
              <Button variant="ghost" size="sm">Lihat Semua</Button>
            </Link>
          </CardHeader>
          <CardContent>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead><tr className="border-b border-slate-100">
                  <th className="text-left py-3 px-4 text-slate-500 font-medium">Nama</th>
                  <th className="text-left py-3 px-4 text-slate-500 font-medium">Subdomain</th>
                  <th className="text-left py-3 px-4 text-slate-500 font-medium">Status</th>
                </tr></thead>
                <tbody>
                  {(data?.recent_tenants || []).map((t, i) => (
                    <tr key={i} className="border-b border-slate-50 hover:bg-slate-50/50">
                      <td className="py-3 px-4 font-medium text-slate-900">{t.name}</td>
                      <td className="py-3 px-4 text-slate-600">{t.subdomain}</td>
                      <td className="py-3 px-4"><Badge className={statusColor(t.subscription_status)}>{t.subscription_status}</Badge></td>
                    </tr>
                  ))}
                  {(!data?.recent_tenants?.length) && <tr><td colSpan={3} className="py-8 text-center text-slate-400">Belum ada tenant</td></tr>}
                </tbody>
              </table>
            </div>
          </CardContent>
        </Card>

        {/* Recent Subscriptions */}
        <Card className="border-slate-200 shadow-sm">
          <CardHeader className="flex flex-row items-center justify-between">
            <CardTitle className="text-lg">Subscription Terbaru</CardTitle>
            <Link to="/admin/billing">
              <Button variant="ghost" size="sm">Lihat Semua</Button>
            </Link>
          </CardHeader>
          <CardContent>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead><tr className="border-b border-slate-100">
                  <th className="text-left py-3 px-4 text-slate-500 font-medium">Tenant</th>
                  <th className="text-left py-3 px-4 text-slate-500 font-medium">Paket</th>
                  <th className="text-left py-3 px-4 text-slate-500 font-medium">Status</th>
                  <th className="text-right py-3 px-4 text-slate-500 font-medium">Jumlah</th>
                </tr></thead>
                <tbody>
                  {(data?.recent_subscriptions || []).map((s, i) => (
                    <tr key={i} className="border-b border-slate-50 hover:bg-slate-50/50">
                      <td className="py-3 px-4 font-medium text-slate-900">{s.tenant?.name || '-'}</td>
                      <td className="py-3 px-4 text-slate-600">{s.package_name}</td>
                      <td className="py-3 px-4"><Badge className={statusColor(s.status)}>{s.status}</Badge></td>
                      <td className="py-3 px-4 text-right font-medium">Rp {fmt(s.amount)}</td>
                    </tr>
                  ))}
                  {(!data?.recent_subscriptions?.length) && <tr><td colSpan={4} className="py-8 text-center text-slate-400">Belum ada subscription</td></tr>}
                </tbody>
              </table>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Quick Actions */}
      <div className="mt-6 grid grid-cols-2 md:grid-cols-4 gap-4">
        <Link to="/admin/tenants">
          <Card className="border-slate-200 shadow-sm hover:shadow-md transition-shadow cursor-pointer">
            <CardContent className="p-4 flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-blue-50 flex items-center justify-center">
                <Users size={20} className="text-[#2D6CDF]" />
              </div>
              <div>
                <p className="font-medium text-slate-900">Kelola Tenant</p>
                <p className="text-xs text-slate-500">{stats.total_tenants} total</p>
              </div>
            </CardContent>
          </Card>
        </Link>
        <Link to="/admin/packages">
          <Card className="border-slate-200 shadow-sm hover:shadow-md transition-shadow cursor-pointer">
            <CardContent className="p-4 flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-emerald-50 flex items-center justify-center">
                <Package size={20} className="text-emerald-600" />
              </div>
              <div>
                <p className="font-medium text-slate-900">Paket SaaS</p>
                <p className="text-xs text-slate-500">{stats.total_packages} paket</p>
              </div>
            </CardContent>
          </Card>
        </Link>
        <Link to="/admin/billing">
          <Card className="border-slate-200 shadow-sm hover:shadow-md transition-shadow cursor-pointer">
            <CardContent className="p-4 flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-violet-50 flex items-center justify-center">
                <CreditCard size={20} className="text-violet-600" />
              </div>
              <div>
                <p className="font-medium text-slate-900">Billing</p>
                <p className="text-xs text-slate-500">{stats.pending_payments} pending</p>
              </div>
            </CardContent>
          </Card>
        </Link>
        <Link to="/admin/affiliates">
          <Card className="border-slate-200 shadow-sm hover:shadow-md transition-shadow cursor-pointer">
            <CardContent className="p-4 flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-teal-50 flex items-center justify-center">
                <Share2 size={20} className="text-teal-600" />
              </div>
              <div>
                <p className="font-medium text-slate-900">Affiliates</p>
                <p className="text-xs text-slate-500">{stats.total_saas_affiliates} affiliate</p>
              </div>
            </CardContent>
          </Card>
        </Link>
      </div>
    </div>
  );
}
